<template>
  <s-page class="max-w-3xl" v-if="isDataLoaded">
    <div
      class="font-degular font-extralight leading-normal text-2xl flex flex-col items-center w-full"
    >
      <header
        class="mb-3 mt-6 px-3 w-full flex-shrink-0 relative bg-gray-400 rounded-lg shadow-lg overflow-hidden"
      >
        <img
          src="@/assets/svg/SLS-Rice-Pattern.svg"
          class="absolute inset-0 w-full h-full object-cover opacity-25"
          aria-hidden="true"
        />
        <img
          src="@/assets/svg/SLS-PrimaryHoriz-Fire.svg"
          alt="SEOULSPICE Logo"
          class="mx-auto w-full h-full pt-5 px-4 sm:px-6 relative"
        />
        <div class="relative text-gray-900 py-3 flex flex-wrap justify-around">
          <div
            v-for="item in headerItems"
            @click="item.click"
            :key="item.label"
            class="text-center truncate m-2"
          >
            <span
              class="block uppercase tracking-wide text-gray-800 text-xs font-bold"
            >
              {{ item.label }}
            </span>
            <span
              class="block text-2xl sm:text-3xl font-medium text-gray-900 leading-none"
            >
              {{ item.value }}
            </span>
            <span
              v-if="item.subtext"
              class="block tracking-wide text-sm text-gray-800 font-medium"
            >
              {{ item.subtext }}
            </span>
          </div>
        </div>
      </header>

      <section class="pb-3 pt-3 w-full">
        <div
          class="p-2 pt-4 mx-auto flex flex-wrap justify-around bg-gray-300 rounded-lg shadow-lg"
        >
          <!-- <h1
            class="text-2xl sm:text-3xl md:text-4xl leading-tight text-center flex flex-row justify-center font-normal"
          >
            <span>Weekly</span>
          </h1> -->

          <trend-chart-dashboard />
        </div>
      </section>

      <section
        class="pb-3 pt-3 w-full flex flex-wrap items-center justify-center"
      >
        <dashboard-tile
          v-for="shift in shiftLogsPastDue"
          :key="`dashboardTileShift_${shift}`"
          unicon="exclamation-triangle"
          color="red"
          :label="`${capitalize(shifts[shift])} Tasks`"
          :value="tasksRemainingByShifts[shift].length"
          :router-link="`/tasks/${shifts[shift]}`"
        />
        <dashboard-tile
          v-if="
            tasksRemainingByShifts[currentShift] &&
            tasksRemainingByShifts[currentShift].length > 0
          "
          unicon="check-square"
          color="gray"
          :label="`${capitalize(currentShiftName)} Tasks`"
          :value="tasksRemainingByShifts[currentShift].length"
          router-link="/tasks"
          :key="`dashboardTileShift_${currentShift}`"
        />
        <dashboard-tile
          v-if="orderItemsRemaining.length > 0"
          unicon="redo"
          color="gray"
          label="Daily Count"
          :value="orderItemsRemaining.length"
          router-link="/ordering"
          key="dashboardTileOrder"
        />
        <dashboard-tile
          v-if="currentInvoicesRemainingCount > 0"
          unicon="bill"
          color="gray"
          label="Invoices"
          :value="currentInvoicesRemainingCount"
          router-link="/invoicing/receive"
          key="dashboardTileInvoice"
        />
        <dashboard-tile
          v-if="
            isManager &&
            isCurrentInventoryOpen &&
            inventoryItemsRemaining.length > 0
          "
          unicon="file-edit-alt"
          color="gray"
          label="Inventory Count"
          :value="inventoryItemsRemaining.length"
          router-link="/inventory"
          key="dashboardTileShiftInventory"
        />
      </section>

      <section class="pb-3 pt-3 w-full">
        <div
          class="mx-auto flex flex-wrap justify-around bg-gray-400 rounded-lg shadow-lg p-2"
        >
          <div v-for="button in filteredButtons" :key="button.id">
            <s-button
              color="gray"
              :shade="600"
              :shade-hover="700"
              class="m-1 shadow-lg flex flex-auto text-center font-bold"
              @click="button.action"
            >
              {{ button.text }}
            </s-button>
          </div>
        </div>
      </section>
    </div>
    <modal-vendor-select />
    <modal-submit-order />
    <modal-waste />
    <modal-product-transfer />
  </s-page>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { capitalize } from '@/filters'
import DashboardTile from '@/components/DashboardTile'
import TrendChartDashboard from '@/components/TrendChartDashboard'
import SPage from '@/components/SPage'
import SButton from '@/components/Button'
import ModalVendorSelect from '@/components/ModalVendorSelect'
import ModalSubmitOrder from '@/components/ModalSubmitOrder'
import ModalWaste from '@/components/ModalWaste'
import ModalProductTransfer from '@/components/ModalProductTransfer'

export default {
  name: 'Dashboard',
  components: { DashboardTile, SPage, SButton, TrendChartDashboard, ModalVendorSelect, ModalSubmitOrder, ModalWaste, ModalProductTransfer },
  data() {
    return {}
  },
  computed: {
    ...mapGetters('global', ['settingsStoreIds']),
    ...mapGetters('user', [
      'stores',
      'isStaff',
      'isManager',
      'isAdmin',
      'isDeveloper',
      'storeName'
    ]),
    ...mapGetters('config', ['isDataLoaded']),
    ...mapGetters('time', ['locationTime', 'timezone']),
    ...mapGetters('storeModule/inventories', ['inventoriesOrdered']),
    ...mapGetters('storeModule', [
      'name',
      'tasksRemainingByShift',
      'tasksRemainingByShifts',
      'isCurrentInventoryOpen',
      'inventoryItemsRemaining',
      'shifts',
      'currentInvoicesRemaining',
      'currentInvoicesRemainingCount',
      'timezone',
      'currentInventory',
      'currentOrderCompleted'
    ]),
    ...mapGetters([
      'currentShift',
      'currentShiftName',
      'currentShiftHours',
      'orderItemsRemaining',
      'orderItemsToday',
      'shiftLogsPastDue',
      'isTodayInventoryDay'
    ]),
    isOrdersLocked() {
      if (this.isTodayInventoryDay) {
        if (this.currentInventory && this.currentInventory.completed) return false
        // Fallback: currentInventoryId may have been cleared after submission;
        // check if any inventory was completed within the past week
        const oneWeekAgo = Date.now() / 1000 - 7 * 24 * 60 * 60
        if (
          this.inventoriesOrdered.some(
            (inv) => inv.completed && inv.date && inv.date.seconds > oneWeekAgo
          )
        )
          return false
        return true
      }
      if (this.orderItemsToday && this.orderItemsToday.length > 0) {
        return !this.currentOrderCompleted
      }
      return false
    },
    buttons() {
      return [
        {
          text: 'Create Order(s)',
          condition: this.isManager,
          action: () => {
            if (this.isOrdersLocked) {
              this.$modal.show('modal--message', {
                message: `Please complete today's ${this.isTodayInventoryDay ? 'inventory' : 'count'} before placing orders.`
              })
            } else {
              this.$modal.show('modal--vendor-select')
            }
          }
        },
        {
          text: 'Create Waste Log',
          condition: this.isStaff,
          action: () => this.$modal.show('modal--waste')
        },
        {
          text: 'Request Transfer',
          condition: this.isStaff,
          action: () => this.$modal.show('modal--product-transfer')
        },
        {
          text: 'Reset Daily Tasks',
          condition: this.isAdmin,
          action: () => this.createNewLog({ type: 'daily' })
        },
        {
          text: 'Reset Weekly Tasks',
          condition: this.isAdmin,
          action: () => this.createNewLog({ type: 'weekly' })
        },
        {
          text: 'Reset Monthly Tasks',
          condition: this.isAdmin,
          action: () => this.createNewLog({ type: 'monthly' })
        },
        {
          text: this.isTodayInventoryDay ? 'New Inventory' : 'New Count',
          condition: this.isAdmin,
          action: () => {
            this.createNewOrder()
              .then((result) => {
                if (result && result.message) {
                  this.$modal.show('modal--message', {
                    message: result.message
                  })
                }
              })
              .catch((error) => {
                console.error('Error creating new order:', error)
                this.$modal.show('modal--message', {
                  message: 'Error creating new order.'
                })
              })
          }
        },
        {
          text: 'Generate Weekly Report',
          condition: this.isDeveloper,
          action: () => this.$modal.show('modal--generate-report')
        }
      ]
    },
    filteredButtons() {
      return this.buttons.filter((button) => button.condition)
    },
    headerItems() {
      return [
        {
          label: 'Location',
          value: this.capitalize(this.name),
          click: this.openStoreSelector
        },
        {
          label: 'Day Part',
          value: this.capitalize(this.currentShiftName),
          subtext: this.currentShiftHours,
          click: () => {}
        },
        {
          label: 'Location Time',
          value: this.locationTime,
          click: () => {}
        }
      ]
    }
  },
  methods: {
    ...mapActions(['createNewOrder']),
    ...mapActions('storeModule', ['createNewLog']),
    capitalize,
    openStoreSelector() {
      if (this.isManager && this.stores.length > 1) {
        this.$modal.show('modal--select-store', {
          list: this.stores.slice().sort((a, b) => {
            const nameA = this.settingsStoreIds[a]
              ? this.settingsStoreIds[a].name
              : a
            const nameB = this.settingsStoreIds[b]
              ? this.settingsStoreIds[b].name
              : b
            return nameA.toLowerCase().localeCompare(nameB.toLowerCase())
          }),
          display: (e) =>
            e && this.settingsStoreIds[e] && this.settingsStoreIds[e].name
              ? this.settingsStoreIds[e].name
              : '',
          currentStore: this.storeName
        })
      }
    }
  }
}
</script>

<style>
.sls-logo-path {
  fill: #e70000;
  stroke-width: 0px;
}
</style>
